import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Settings, Filter, Hash, Share2 } from 'lucide-react';
import type { Sip } from '@sbclite/core';
import { RoutingRuleCreateInputStrictSchema } from '@sbclite/core/schemas';
import { useCreateRoutingRule, useUpdateRoutingRule } from '../../../hooks/useRouting';
import { createFormErrorHandler } from '../../../utils/form-error-handler';
import { ResourceEditor, firstTabWithError, type ResourceEditorTab } from '../../common';
import {
  buildDefaultValues,
  toApiData,
  getUnsupportedDestinationType,
  type RoutingRuleFormData,
  type TemplateKey,
} from './schema';
import { GeneralTab } from './tabs/GeneralTab';
import { SourceTab } from './tabs/SourceTab';
import { NumbersTab } from './tabs/NumbersTab';
import { DestinationTab } from './tabs/DestinationTab';

interface RoutingRuleEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSaved?: (id: string) => void;
  initialValues?: Partial<RoutingRuleFormData>;
  /** When set, the editor is in edit mode. */
  editingRule?: Sip.RoutingRuleInfo | null;
  /** Create-time template seed (ignored when editingRule is set). */
  templateKey?: TemplateKey | null;
}

export function RoutingRuleEditor({ isOpen, onClose, onSuccess, onSaved, initialValues, editingRule, templateKey }: RoutingRuleEditorProps) {
  const [activeTab, setActiveTab] = useState('general');

  // Destination type the console editor cannot edit (e.g. gateway/pstn/forward).
  // When set, the Destination tab shows a warning and preserves the original.
  const unsupportedDestinationType = editingRule ? getUnsupportedDestinationType(editingRule) : null;

  const formErrorHandler = createFormErrorHandler({
    componentName: 'RoutingRuleEditor',
    showFieldErrors: true,
  });

  const form = useForm<RoutingRuleFormData>({
    resolver: zodResolver(RoutingRuleCreateInputStrictSchema),
    defaultValues: buildDefaultValues({ editingRule, templateKey }),
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset({ ...buildDefaultValues({ editingRule, templateKey }), ...(!editingRule ? initialValues : {}) });
    setActiveTab('general');
  }, [isOpen, editingRule, templateKey, initialValues, form]);

  const createRuleMutation = useCreateRoutingRule();
  const updateRuleMutation = useUpdateRoutingRule();

  const handleSuccess = () => {
    onSuccess?.();
    onClose();
    form.reset(buildDefaultValues({ editingRule: null, templateKey: null }));
    setActiveTab('general');
  };

  const onSubmit = (data: RoutingRuleFormData) => {
    const apiData = toApiData(data);

    const onMutationSuccess = (response: { success: boolean; data?: unknown }) => {
      if (response.success && response.data) {
        const identity = z.object({ id: z.string().uuid() }).safeParse(response.data);
        if (identity.success) onSaved?.(identity.data.id);
        handleSuccess();
      } else {
        // API returned 2xx with a failure envelope — surface it instead of silently staying open.
        formErrorHandler.onError(new Error(`Failed to ${editingRule ? 'update' : 'create'} routing rule`));
      }
    };

    if (editingRule) {
      updateRuleMutation.mutate(
        { ruleId: editingRule.id, updates: apiData },
        { onSuccess: onMutationSuccess, onError: formErrorHandler.onError }
      );
    } else {
      createRuleMutation.mutate(apiData, {
        onSuccess: onMutationSuccess,
        onError: formErrorHandler.onError,
      });
    }
  };

  const tabs: ResourceEditorTab[] = useMemo(
    () => [
      {
        id: 'general',
        label: 'General',
        icon: Settings,
        fields: ['name', 'priority', 'hostId', 'description', 'enabled'],
        render: () => <GeneralTab form={form} />,
      },
        {
          id: 'source',
          label: 'Source',
          icon: Filter,
          fields: ['sourceScope', 'sourcePeerId', 'sourcePeerGroupId', 'conditions'],
          render: () => <SourceTab form={form} />,
        },
      {
        id: 'numbers',
        label: 'Numbers',
        icon: Hash,
        fields: ['numberPattern'],
        render: () => <NumbersTab form={form} />,
      },
      {
        id: 'destination',
        label: 'Destination',
        icon: Share2,
        fields: ['destinationType', 'destinationTarget', 'inboundManipulationId', 'outboundManipulationId'],
        render: () => (
          <DestinationTab
            form={form}
            unsupportedDestinationType={unsupportedDestinationType}
          />
        ),
      },
    ],
    [form, unsupportedDestinationType]
  );

  const onInvalid = (errors: typeof form.formState.errors) => {
    const target = firstTabWithError(errors, tabs);
    if (target) setActiveTab(target);
    formErrorHandler.onInvalid(errors);
  };

  const isSaving = createRuleMutation.isPending || updateRuleMutation.isPending;

  return (
    <ResourceEditor
      isOpen={isOpen}
      onClose={onClose}
      title={editingRule ? 'Edit Routing Rule' : 'Create Routing Rule'}
      subtitle={
        editingRule
          ? `Editing: ${editingRule.name}`
          : 'Configure call routing logic and SIP request handling'
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      errors={form.formState.errors}
      onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      onCancel={onClose}
      isSaving={isSaving}
      saveLabel={editingRule ? 'Update Rule' : 'Create Rule'}
      savingLabel={editingRule ? 'Updating...' : 'Creating...'}
    />
  );
}

export default RoutingRuleEditor;
